import { useCallback, useState } from "react";

import { CloseIconSlimButton } from "@/design-system/icons/close-icon-slim";
import Magnifier from "@/design-system/icons/magnifier.svg";
import { useOutsideClick } from "@/lib/hooks/use-outside-click";
import { sendTelemetryEvent } from "@/lib/telemetry";
import { languageSelector } from "@/src/contexts";
import { useOnSearchFormSubmit, useOnSearchInputChange } from "@/src/shared/Header/header-search-helper";
import useSearchHelper from "@/src/shared/Header/hooks/use-search-helper";
import { searchQueryParam } from "@Constants/PortalConstants";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type PropsWithStyleX, colors } from "./../../../styles/tokens.stylex";
import { SearchBarResources } from "./SearchBar.Resources";
import { SearchBarResourcesEn } from "./SearchBar.Resources.En";
import HeaderSearchResults from "./header-search-results";

export default function HeaderSearch({ styleX }: PropsWithStyleX) {
    const resources = languageSelector(SearchBarResources, SearchBarResourcesEn);
    const [showResults, setShowResults] = useState(false);

    const { inputValue, setSearchText, inputRef } = useOnSearchInputChange();
    useSearchHelper(setShowResults, inputRef);

    const memoizedCallback = useCallback(() => {
        if (showResults) {
            setShowResults(false);
            if (inputRef?.current) inputRef.current.blur();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showResults]);

    const sectionRef = useOutsideClick<HTMLDivElement>(memoizedCallback);

    const onSubmit = useOnSearchFormSubmit();

    const showClear = inputValue.length > 0;

    return (
        <section {...stylex.props(styles.container, styleX)} ref={sectionRef}>
            <form action="/szukaj" method="get" onSubmit={onSubmit}>
                <img {...stylex.props(styles.icon)} src={Magnifier} alt="Szukaj w goodie" />
                <input
                    {...stylex.props(styles.input)}
                    ref={inputRef}
                    name={searchQueryParam}
                    type="text"
                    enterKeyHint="search"
                    onFocus={() => setShowResults(true)}
                    placeholder={resources.searchWatermark}
                    defaultValue={inputValue}
                    onChange={(ev) => setSearchText(ev.currentTarget.value.substring(0, 1000))}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                            e.currentTarget.form?.requestSubmit();
                            e.preventDefault();
                        }
                    }}
                    data-testid="hdr-srchbar"
                    aria-label={resources.searchWatermark}
                />
                <button type="submit" style={{ display: "none" }} />

                {inputValue && (
                    <CloseIconSlimButton
                        type="button"
                        styleX={[styles.clearBtn, showClear && styles.showClear]}
                        onClick={() => {
                            if (inputRef.current) {
                                inputRef.current.value = "";
                                setSearchText("");
                                requestAnimationFrame(() => inputRef.current.focus());
                            }

                            sendTelemetryEvent({
                                eventName: "search-keywordsClear",
                                origin: "searchResultsGlobal",
                                actionType: "Click",
                            });
                        }}
                        aria-hidden={!showClear}
                    />
                )}
            </form>

            {showResults && (
                <div {...stylex.props(styles.searchResult)}>
                    <HeaderSearchResults
                        onClick={(value) => {
                            setSearchText(value);
                        }}
                        text={inputValue}
                    />
                </div>
            )}
        </section>
    );
}

const styles = stylex.create({
    container: {
        borderRadius: "1.25em",
        gap: "0.625em",
        fontSize: "1rem",
        position: "relative",
        zIndex: 1,
    },

    icon: {
        pointerEvents: "none",
        position: "absolute",
        userSelect: "none",
        zIndex: 0,
        height: "1.25em",
        left: "0.625em",
        top: "0.5em",
        width: "1.25em",
    },

    input: {
        borderRadius: "1.25em",
        borderStyle: "none",
        outline: "none",
        backgroundColor: "#efefef",
        backgroundImage: "none",
        boxSizing: "border-box",
        lineHeight: "normal",
        paddingBottom: ".625em",
        paddingLeft: "2.5em",
        paddingRight: "2em",
        paddingTop: ".625em",
        width: "100%",
    },

    clearBtn: {
        color: colors.fg_BrandNavy,
        lineHeight: 1,
        opacity: 0,
        position: "absolute",
        transitionDuration: "0.25s",
        transitionProperty: "opacity",
        transitionTimingFunction: "ease-in-out",
        zIndex: 0,
        height: "0.8125em",
        right: "0.625em",
        top: "0.79em",
        width: "0.8125em",
    },

    showClear: {
        opacity: 1,
    },

    searchResult: {
        inset: "3rem -9rem auto",
        padding: "1.25rem",
        borderColor: "#cecece",
        borderRadius: "1.25em",
        borderStyle: "solid",
        borderWidth: 1,
        gap: ComposableSpacing.s5,
        alignItems: "start",
        backgroundColor: "#fff",
        boxShadow: "0 1px 6px 0 #0000001A",
        color: colors.fg_Primary,
        display: "flex",
        flexDirection: "column",
        position: "absolute",
        zIndex: -1,
    },
});
